import React, { forwardRef, useState } from 'react';
import {
  Box, Modal, SxProps, Theme,
} from '@mui/material';

interface PreviewImageProps {
  src: string;
  alt?: string;
  openInModal?: boolean;
  sx?: SxProps<Theme>;
}

export interface PreviewImageRef {
  open: () => void;
  close: () => void;
}

const PreviewImage = forwardRef<PreviewImageRef, PreviewImageProps>(({
  src,
  alt = 'Image',
  openInModal = false,
  sx = {},
}, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  React.useImperativeHandle(ref, () => ({
    open: () => setIsModalOpen(true),
    close: () => setIsModalOpen(false),
  }));

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Render the thumbnail with click handler for modal mode
  const renderThumbnail = () => (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        overflow: 'hidden',
        ...sx,
      }}
      onClick={handleOpenModal}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </Box>
  );

  // Render the modal with full-size image
  const renderModal = () => (
    <Modal
      open={isModalOpen}
      onClose={handleCloseModal}
      aria-labelledby="image-preview-modal"
      aria-describedby="full-size-image-preview"
      onClick={handleCloseModal}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '90vw',
          maxHeight: '90vh',
          outline: 'none',
          bgcolor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
    </Modal>
  );

  return (openInModal ? (
    <>
      {renderThumbnail()}
      {renderModal()}
    </>
  ) : (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        ...sx,
      }}
    />
  ));
});

PreviewImage.displayName = 'PreviewImage';

PreviewImage.defaultProps = {
  alt: 'Image',
  openInModal: false,
  sx: {},
};

export default PreviewImage;
